import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { useLazyQuery, useLazyCommand, useEvent, useApiErrors } from '@soap/modules';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { UnarchiveStudentDialog } from '../components/dialogs/UnarchiveStudentDialog';
import {
  getEditStudentInvalidFieldKeys,
  getVisibleDateOfBirthErrors,
  getVisibleEditStudentFieldError,
  isEditStudentFormValid,
  validateEditStudentForm,
} from '../components/editStudentFormValidation';
import {
  emptyStudentDateOfBirth,
  StudentDateOfBirthFields,
  StudentDateOfBirthValue,
  studentDateOfBirthFromFormFieldData,
  studentDateOfBirthFromLegacyIso,
  toEditStudentDateOfBirthCommand,
} from '../components/StudentDateOfBirthFields';
import { cn } from '../lib/utils';

function countStudentCurriculumTopicNodes(data: any): number {
  let n = 0;
  const countTopic = (t: any) => {
    n += 1;
    (t?.e180_ChildTopics || t?.E180_ChildTopics || []).forEach((c: any) => countTopic(c));
  };
  (data?.e180_Subjects || data?.E180_Subjects || []).forEach((sub: any) => {
    (sub?.e180_KeyStages || sub?.E180_KeyStages || []).forEach((ks: any) => {
      (ks?.e180_Years || ks?.E180_Years || []).forEach((y: any) => {
        (y?.e180_Topics || y?.E180_Topics || []).forEach((t: any) => countTopic(t));
      });
    });
  });
  return n;
}

function slimCurriculumIndicatesTopicsLoaded(event: any): boolean {
  if (countStudentCurriculumTopicNodes(event) > 0) return true;

  let totalLeaves = 0;
  const sumYear = (sub: any) => {
    (sub?.e180_KeyStages || sub?.E180_KeyStages || []).forEach((ks: any) => {
      (ks?.e180_Years || ks?.E180_Years || []).forEach((y: any) => {
        const c = y?.e180_TotalTopicCount ?? y?.E180_TotalTopicCount;
        if (typeof c === 'number' && c > 0) totalLeaves += c;
      });
    });
  };
  (event?.e180_Subjects || event?.E180_Subjects || []).forEach(sumYear);
  if (totalLeaves > 0) return true;

  const flag = event?.e180_HasTopicBuckets ?? event?.E180_HasTopicBuckets;
  return flag === true;
}

interface Tag {
  id: string;
  title: string;
}

interface CreateTagDialogProps {
  onClose: () => void;
  onCreate: (tagName: string, tagDescription: string) => void;
}

function CreateTagDialog({ onClose, onCreate }: CreateTagDialogProps) {
  const [tagName, setTagName] = useState('');
  const [tagDescription, setTagDescription] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (tagName.trim()) {
      onCreate(tagName.trim(), tagDescription.trim());
      setTagName('');
      setTagDescription('');
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4">
        <CardHeader>
          <CardTitle>Create New Tag</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tagName">Tag Name</Label>
              <Input
                id="tagName"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                required
                placeholder="Enter tag name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tagDescription">Description (Optional)</Label>
              <Input
                id="tagDescription"
                value={tagDescription}
                onChange={(e) => setTagDescription(e.target.value)}
                placeholder="Enter description"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">Create Tag</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export function EditStudentPage() {
  const { id } = useParams<{ id: string }>();
  const viewStates = {
    loading: 0,
    ready: 1,
    submitting: 2
  };

  const [viewState, setViewState] = useState(viewStates.loading);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [hasUserId, setHasUserId] = useState(false);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [showCreateTag, setShowCreateTag] = useState(false);
  const [showUnarchiveStudentDialog, setShowUnarchiveStudentDialog] = useState(false);
  const [unarchiveStudentId, setUnarchiveStudentId] = useState<string | null>(null);
  const [unarchiveStudentName, setUnarchiveStudentName] = useState<string>('');
  const [dateOfBirth, setDateOfBirth] = useState<StudentDateOfBirthValue>(emptyStudentDateOfBirth());
  const [showErrorsFor, setShowErrorsFor] = useState<Set<string>>(() => new Set());
  const [gender, setGender] = useState('');
  const [strengths, setStrengths] = useState('');
  const [challengesAndSupportNeeds, setChallengesAndSupportNeeds] = useState('');
  const [learningSpeed, setLearningSpeed] = useState('');
  const [readingAbility, setReadingAbility] = useState('');
  const [activities, setActivities] = useState<string[]>([]);
  const [newActivity, setNewActivity] = useState('');
  const [stories, setStories] = useState<string[]>([]);
  const [newStory, setNewStory] = useState('');
  const [peoplePlacesAndThings, setPeoplePlacesAndThings] = useState('');
  const [rewards, setRewards] = useState<string[]>([]);
  const [newReward, setNewReward] = useState('');
  const [socialStyle, setSocialStyle] = useState('');
  const [approachToNewThings, setApproachToNewThings] = useState('');
  const [preferredLearningWorkStyle, setPreferredLearningWorkStyle] = useState('');
  const [frustrationTolerance, setFrustrationTolerance] = useState('');
  const [motivationStyle, setMotivationStyle] = useState<string[]>([]);
  const [passFailThreshold, setPassFailThreshold] = useState<string>('');
  const [educationSystemKey, setEducationSystemKey] = useState('');
  const [educationSystemDisplayName, setEducationSystemDisplayName] = useState('');
  const [educationSystemOptions, setEducationSystemOptions] = useState<{ key: string; displayName: string }[]>([]);
  const [curriculumHasTopics, setCurriculumHasTopics] = useState(false);

  const [getFormData] = useLazyQuery('C162v1_GetC161FormData');
  const [getStudentCurriculum] = useLazyQuery('C260v1_GetStudentCurriculum');
  const [getEducationSystems] = useLazyCommand('C311v1_GetEducationSystems');
  const [sendCommand] = useLazyCommand('C161v1_EditStudent');
  const [createTagCommand] = useLazyCommand('C120v1_AddTag');
  const { errors } = useApiErrors();

  const formValues = useMemo(
    () => ({
      firstName,
      lastName,
      email,
      hasUserId,
      dateOfBirth,
      educationSystemKey,
      passFailThreshold,
    }),
    [firstName, lastName, email, hasUserId, dateOfBirth, educationSystemKey, passFailThreshold]
  );

  const formErrors = useMemo(() => validateEditStudentForm(formValues), [formValues]);
  const isFormValid = useMemo(() => isEditStudentFormValid(formErrors), [formErrors]);

  const markFieldTouched = useCallback((fieldKey: string) => {
    setShowErrorsFor((current) => {
      if (current.has(fieldKey)) return current;
      const next = new Set(current);
      next.add(fieldKey);
      return next;
    });
  }, []);

  const visibleDateOfBirthErrors = useMemo(
    () => getVisibleDateOfBirthErrors(showErrorsFor, formErrors.dateOfBirth),
    [showErrorsFor, formErrors.dateOfBirth]
  );

  // Fetch student data on mount
  useEffect(() => {
    if (id) {
      getFormData({
        C162_StudentId: id
      });
      void getStudentCurriculum({ C260_StudentId: id });
      void getEducationSystems({});
    }
  }, [id, getFormData, getStudentCurriculum, getEducationSystems]);

  useEvent({
    eventName: 'E311v1_GotEducationSystems',
    onEventReceived: (event: any) => {
      const raw = event?.e311_Systems || event?.E311_Systems || [];
      const mapped = (Array.isArray(raw) ? raw : [])
        .map((row: any) => ({
          key: String(row?.e311_Key ?? row?.E311_Key ?? '').trim(),
          displayName: String(row?.e311_DisplayName ?? row?.E311_DisplayName ?? '').trim()
        }))
        .filter((o: { key: string }) => o.key);
      setEducationSystemOptions(mapped);
    }
  });

  useEffect(() => {
    if (curriculumHasTopics) return;
    if (educationSystemKey !== '') return;
    if (educationSystemOptions.length === 0) return;
    const first = educationSystemOptions[0];
    setEducationSystemKey(first.key);
    setEducationSystemDisplayName(first.displayName);
  }, [curriculumHasTopics, educationSystemKey, educationSystemOptions]);

  useEvent({
    eventName: 'E180v1_GotStudentCurriculum',
    onEventReceived: (event: any) => {
      setCurriculumHasTopics(slimCurriculumIndicatesTopicsLoaded(event));
    }
  });

  // Listen for form data event
  useEvent({
    eventName: "E134v1_GotC161FormData",
    onEventReceived: (event: any) => {
      const fieldData = event.e000_FieldData || [];

      const firstNameField = fieldData.find((field: any) => field.name === 'c161_FirstName');
      const lastNameField = fieldData.find((field: any) => field.name === 'c161_LastName');
      const emailField = fieldData.find((field: any) => field.name === 'c161_Email');
      const hasUserIdField = fieldData.find((field: any) => field.name === 'c161_HasUserId');
      const tagsField = fieldData.find((field: any) => field.name === 'c161_Tags');
      const birthdayField = fieldData.find((field: any) => field.name === 'c161_Birthday');
      const genderField = fieldData.find((field: any) => field.name === 'c161_Gender');
      const strengthsField = fieldData.find((field: any) => field.name === 'c161_Strengths');
      const challengesField = fieldData.find((field: any) => field.name === 'c161_ChallengesAndSupportNeeds');
      const learningSpeedField = fieldData.find((field: any) => field.name === 'c161_LearningSpeed');
      const readingAbilityField = fieldData.find((field: any) => field.name === 'c161_ReadingAbility');
      const activitiesField = fieldData.find((field: any) => field.name === 'c161_Activities');
      const storiesField = fieldData.find((field: any) => field.name === 'c161_Stories');
      const peoplePlacesField = fieldData.find((field: any) => field.name === 'c161_PeoplePlacesAndThings');
      const rewardsField = fieldData.find((field: any) => field.name === 'c161_Rewards');
      const socialStyleField = fieldData.find((field: any) => field.name === 'c161_SocialStyle');
      const approachField = fieldData.find((field: any) => field.name === 'c161_ApproachToNewThings');
      const preferredStyleField = fieldData.find((field: any) => field.name === 'c161_PreferredLearningWorkStyle');
      const frustrationField = fieldData.find((field: any) => field.name === 'c161_FrustrationTolerance');
      const motivationField = fieldData.find((field: any) => field.name === 'c161_MotivationStyle');
      const passFailThresholdField = fieldData.find((field: any) => field.name === 'c161_PassFailThreshold');

      const loadedFirstName = firstNameField?.initialValue || '';
      const loadedLastName = lastNameField?.initialValue || '';
      const loadedEmail = emailField?.initialValue || '';
      const loadedHasUserId = hasUserIdField?.initialValue || false;

      let loadedDateOfBirth = studentDateOfBirthFromFormFieldData(fieldData, '161');
      if (!loadedDateOfBirth.month && !loadedDateOfBirth.year) {
        const birthdayValue = birthdayField?.initialValue;
        if (birthdayValue) {
          loadedDateOfBirth = studentDateOfBirthFromLegacyIso(birthdayValue);
        }
      }

      const esKeyField = fieldData.find((field: any) => field.name === 'c161_EducationSystemKey');
      const esNameField = fieldData.find((field: any) => field.name === 'c161_EducationSystemDisplayName');
      const loadedEducationSystemKey = esKeyField?.initialValue ? String(esKeyField.initialValue) : '';
      const loadedEducationSystemDisplayName = esNameField?.initialValue ? String(esNameField.initialValue) : '';
      const loadedPassFailThreshold =
        passFailThresholdField?.initialValue != null ? String(passFailThresholdField.initialValue) : '50';

      setFirstName(loadedFirstName);
      setLastName(loadedLastName);
      setEmail(loadedEmail);
      setHasUserId(loadedHasUserId);
      setDateOfBirth(loadedDateOfBirth);
      
      setGender(genderField?.initialValue || '');
      setStrengths(strengthsField?.initialValue || '');
      setChallengesAndSupportNeeds(challengesField?.initialValue || '');
      setLearningSpeed(learningSpeedField?.initialValue || '');
      setReadingAbility(readingAbilityField?.initialValue || '');
      // Activities, Stories, and Rewards are pipe-separated strings
      const activitiesValue = activitiesField?.initialValue;
      setActivities(typeof activitiesValue === 'string' && activitiesValue ? activitiesValue.split('|').filter(Boolean) : []);
      const storiesValue = storiesField?.initialValue;
      setStories(typeof storiesValue === 'string' && storiesValue ? storiesValue.split('|').filter(Boolean) : []);
      setPeoplePlacesAndThings(peoplePlacesField?.initialValue || '');
      const rewardsValue = rewardsField?.initialValue;
      setRewards(typeof rewardsValue === 'string' && rewardsValue ? rewardsValue.split('|').filter(Boolean) : []);
      setSocialStyle(socialStyleField?.initialValue || '');
      setApproachToNewThings(approachField?.initialValue || '');
      setPreferredLearningWorkStyle(preferredStyleField?.initialValue || '');
      setFrustrationTolerance(frustrationField?.initialValue || '');
      // Motivation Style can be a string (pipe-separated) or array
      const motivationValue = motivationField?.initialValue;
      if (Array.isArray(motivationValue)) {
        setMotivationStyle(motivationValue);
      } else if (typeof motivationValue === 'string' && motivationValue) {
        setMotivationStyle(motivationValue.split('|').filter(Boolean));
      } else {
        setMotivationStyle([]);
      }
      
      setPassFailThreshold(loadedPassFailThreshold);
      setEducationSystemKey(loadedEducationSystemKey);
      setEducationSystemDisplayName(loadedEducationSystemDisplayName);

      const initialErrors = validateEditStudentForm({
        firstName: loadedFirstName,
        lastName: loadedLastName,
        email: loadedEmail,
        hasUserId: loadedHasUserId,
        dateOfBirth: loadedDateOfBirth,
        educationSystemKey: loadedEducationSystemKey,
        passFailThreshold: loadedPassFailThreshold,
      });
      setShowErrorsFor(new Set(getEditStudentInvalidFieldKeys(initialErrors)));

      if (tagsField && tagsField.initialValue) {
        const tagData = tagsField.initialValue;
        const allEnumerations = tagData.AllEnumerations || tagData.allEnumerations || [];
        const selectedKeys = tagData.SelectedKeys || tagData.selectedKeys || [];
        
        const allTags: Tag[] = allEnumerations.map((t: any) => ({
          id: t.Key || t.key,
          title: t.Value || t.value
        }));
        
        const selected = allTags.filter(t => selectedKeys.includes(t.id));
        
        setAvailableTags(allTags);
        setSelectedTags(selected);
      }
      
      setViewState(viewStates.ready);
    }
  });

  // Listen for successful edit event
  useEvent({
    eventName: "E135v1_StudentUpdated",
    onEventReceived: () => {
      location.href = '#/app/profiles/students';
    }
  });

  // Listen for tag added event
  useEvent({
    eventName: "E105v1_TagAdded",
    onEventReceived: (event: any) => {
      if (event.e105_TagTypeKey === "student" && id) {
        getFormData({
          C162_StudentId: id
        });
      }
    }
  }, [id, getFormData]);

  // Listen for email reuse requires unarchive event
  useEvent({
    eventName: "E203v1_EmailReuseRequiresUnarchive",
    onEventReceived: (event: any) => {
      const errorCode = event.e203_ErrorCode;
      if (errorCode === "EmailReuseRequiresUnarchiveStudent" && viewState === viewStates.submitting) {
        setUnarchiveStudentId(event.e203_StudentId);
        // Extract student name from message if available, or use a default
        const message = event.e203_Message || '';
        const nameMatch = message.match(/\(([^)]+)\)/);
        setUnarchiveStudentName(nameMatch ? nameMatch[1] : 'this student');
        setShowUnarchiveStudentDialog(true);
        setViewState(viewStates.ready);
      }
    }
  });

  // Reset submitting state when an error occurs
  useEffect(() => {
    if (errors.length > 0 && viewState === viewStates.submitting) {
      setViewState(viewStates.ready);
    }
  }, [errors, viewState]);

  const handleTagToggle = (tag: Tag) => {
    const isSelected = selectedTags.some(t => t.id === tag.id);
    
    if (isSelected) {
      setSelectedTags(selectedTags.filter(t => t.id !== tag.id));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleCreateTag = async (tagName: string, tagDescription: string) => {
    createTagCommand({
      $type: "C120v1_AddTag",
      C120_TagName: tagName,
      C120_TagDescription: tagDescription,
      C120_TagTypeKey: "student"
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !isFormValid) return;

    setViewState(viewStates.submitting);

    await sendCommand({
      C161_StudentId: id,
      C161_FirstName: firstName,
      C161_LastName: lastName,
      C161_Email: email,
      C161_Tags: {
        SelectedKeys: selectedTags.map(t => t.id)
      },
      C161_DateOfBirth: toEditStudentDateOfBirthCommand(dateOfBirth),
      C161_Gender: gender,
      C161_Strengths: strengths,
      C161_ChallengesAndSupportNeeds: challengesAndSupportNeeds,
      C161_LearningSpeed: learningSpeed,
      C161_ReadingAbility: readingAbility || null,
      C161_Activities: activities.length > 0 ? activities.join('|') : null,
      C161_Stories: stories.length > 0 ? stories.join('|') : null,
      C161_PeoplePlacesAndThings: peoplePlacesAndThings,
      C161_Rewards: rewards.length > 0 ? rewards.join('|') : null,
      C161_SocialStyle: socialStyle,
      C161_ApproachToNewThings: approachToNewThings,
      C161_PreferredLearningWorkStyle: preferredLearningWorkStyle,
      C161_FrustrationTolerance: frustrationTolerance,
      C161_MotivationStyle: motivationStyle.join('|'),
      C161_PassFailThreshold: passFailThreshold ? parseInt(passFailThreshold, 10) : null,
      C161_EducationSystemKey: educationSystemKey,
      C161_EducationSystemDisplayName: educationSystemDisplayName
    });
  };

  const addActivity = () => {
    if (newActivity.trim()) {
      setActivities([...activities, newActivity.trim()]);
      setNewActivity('');
    }
  };

  const removeActivity = (index: number) => {
    setActivities(activities.filter((_, i) => i !== index));
  };

  const addStory = () => {
    if (newStory.trim()) {
      setStories([...stories, newStory.trim()]);
      setNewStory('');
    }
  };

  const removeStory = (index: number) => {
    setStories(stories.filter((_, i) => i !== index));
  };

  const addReward = () => {
    if (newReward.trim()) {
      setRewards([...rewards, newReward.trim()]);
      setNewReward('');
    }
  };

  const removeReward = (index: number) => {
    setRewards(rewards.filter((_, i) => i !== index));
  };

  switch (viewState) {
    case viewStates.loading:
      return (
        <div className="p-4 sm:p-6">
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action-blue"></div>
                <span className="text-gray-600">Loading...</span>
              </div>
            </div>
          </div>
      );

    case viewStates.submitting:
      return (
        <div className="p-4 sm:p-6">
            <Card className="max-w-4xl mx-auto">
              <CardContent className="p-6">
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action-blue"></div>
                    <span className="text-gray-600">Saving changes...</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
      );

    case viewStates.ready:
      return (
        <div className="p-4 sm:p-6">
            <Card className="max-w-2xl mx-auto">
              <CardHeader>
                <div className="flex items-center gap-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => location.href = '#/app/profiles/students'}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <CardTitle className="text-2xl">Edit Student</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        id="firstName"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        onBlur={() => markFieldTouched('firstName')}
                        className={cn(
                          getVisibleEditStudentFieldError('firstName', showErrorsFor, formErrors.firstName) &&
                            'border-destructive-action'
                        )}
                      />
                      {getVisibleEditStudentFieldError('firstName', showErrorsFor, formErrors.firstName) && (
                        <p className="text-sm text-destructive-action">{formErrors.firstName}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        onBlur={() => markFieldTouched('lastName')}
                        className={cn(
                          getVisibleEditStudentFieldError('lastName', showErrorsFor, formErrors.lastName) &&
                            'border-destructive-action'
                        )}
                      />
                      {getVisibleEditStudentFieldError('lastName', showErrorsFor, formErrors.lastName) && (
                        <p className="text-sm text-destructive-action">{formErrors.lastName}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email {hasUserId ? '' : '(Optional)'}</Label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onBlur={() => markFieldTouched('email')}
                        disabled={hasUserId}
                        className={cn(
                          hasUserId ? 'disabled:bg-gray-100 disabled:cursor-not-allowed' : '',
                          getVisibleEditStudentFieldError('email', showErrorsFor, formErrors.email) &&
                            'border-destructive-action'
                        )}
                      />
                      {getVisibleEditStudentFieldError('email', showErrorsFor, formErrors.email) && (
                        <p className="text-sm text-destructive-action">{formErrors.email}</p>
                      )}
                      {hasUserId ? (
                        <p className="text-sm text-gray-500">
                          Email cannot be changed or removed for students with user accounts
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500">
                          Email is optional. Students without emails can still receive assessments via shareable links.
                        </p>
                      )}
                    </div>
                    <StudentDateOfBirthFields
                      idPrefix="edit-student-dob"
                      value={dateOfBirth}
                      onChange={setDateOfBirth}
                      errors={visibleDateOfBirthErrors}
                      onMonthBlur={() => markFieldTouched('dateOfBirth-month')}
                      onYearBlur={() => markFieldTouched('dateOfBirth-year')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Gender</Label>
                    <p className="text-sm text-gray-600 mb-2">Select the child's gender.</p>
                    <div className="space-y-2">
                      {['Male', 'Female', 'Not specified'].map((option) => (
                        <label key={option} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="gender"
                            value={option}
                            checked={gender === option}
                            onChange={(e) => setGender(e.target.value)}
                            className="w-4 h-4 text-action-blue focus:ring-action-blue"
                          />
                          <span className="text-sm text-gray-700">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Learning Style & Reading Ability */}
                  <div id="learning-style-and-reading" className="space-y-4 pt-4 border-t">
                    <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Learning Style & Reading Ability</h3>
                    <div className="space-y-2">
                      <Label htmlFor="strengths">Strengths</Label>
                      <textarea
                        id="strengths"
                        value={strengths}
                        onChange={(e) => setStrengths(e.target.value)}
                        rows={3}
                        placeholder="Describe the child's notable strengths, skills, or areas where they excel."
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-action-blue focus:border-action-blue resize-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="challengesAndSupportNeeds">Challenges and Support Needs</Label>
                      <textarea
                        id="challengesAndSupportNeeds"
                        value={challengesAndSupportNeeds}
                        onChange={(e) => setChallengesAndSupportNeeds(e.target.value)}
                        rows={3}
                        placeholder="Describe any challenges the child experiences and the types of support that help them succeed."
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-action-blue focus:border-action-blue resize-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Learning Speed</Label>
                      <p className="text-sm text-gray-600 mb-2">Select the option that best describes the child's typical learning pace:</p>
                      <div className="space-y-2">
                        {['Above average', 'Average', 'Below average'].map((option) => (
                          <label key={option} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              name="learningSpeed"
                              value={option}
                              checked={learningSpeed === option}
                              onChange={(e) => setLearningSpeed(e.target.value)}
                              className="w-4 h-4 text-action-blue focus:ring-action-blue"
                            />
                            <span className="text-sm text-gray-700">{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Reading Ability</Label>
                      <p className="text-sm text-gray-600 mb-2">Select the option that best describes how the child reads independently:</p>
                      <div className="space-y-2">
                        {[
                          'None',
                          'Learning to read',
                          'Reads below average for age',
                          'Average for age',
                          'Above average for age'
                        ].map((option) => (
                          <label key={option} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              name="readingAbility"
                              value={option}
                              checked={readingAbility === option}
                              onChange={(e) => setReadingAbility(e.target.value)}
                              className="w-4 h-4 text-action-blue focus:ring-action-blue"
                            />
                            <span className="text-sm text-gray-700">{option === 'None' ? 'None (cannot read independently)' : option}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Favourites & Interests */}
                  <div className="space-y-4 pt-4 border-t">
                    <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Favourites & Interests</h3>
                    <div className="space-y-2">
                      <Label>Activities</Label>
                      <div className="flex gap-2">
                        <Input
                          value={newActivity}
                          onChange={(e) => setNewActivity(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addActivity())}
                          placeholder="Add activity"
                          className="flex-1"
                        />
                        <Button type="button" onClick={addActivity} className="px-4">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {activities.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {activities.map((activity, index) => (
                            <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-sm">
                              {activity}
                              <button
                                type="button"
                                onClick={() => removeActivity(index)}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                <X size={14} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Stories</Label>
                      <div className="flex gap-2">
                        <Input
                          value={newStory}
                          onChange={(e) => setNewStory(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addStory())}
                          placeholder="Add story"
                          className="flex-1"
                        />
                        <Button type="button" onClick={addStory} className="px-4">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {stories.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {stories.map((story, index) => (
                            <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-sm">
                              {story}
                              <button
                                type="button"
                                onClick={() => removeStory(index)}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                <X size={14} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="peoplePlacesAndThings">People, Places and Things</Label>
                      <textarea
                        id="peoplePlacesAndThings"
                        value={peoplePlacesAndThings}
                        onChange={(e) => setPeoplePlacesAndThings(e.target.value)}
                        rows={3}
                        placeholder="Describe the people, environments, or objects the child feels especially connected to or enjoys."
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-action-blue focus:border-action-blue resize-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Rewards</Label>
                      <div className="flex gap-2">
                        <Input
                          value={newReward}
                          onChange={(e) => setNewReward(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addReward())}
                          placeholder="Add reward"
                          className="flex-1"
                        />
                        <Button type="button" onClick={addReward} className="px-4">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {rewards.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {rewards.map((reward, index) => (
                            <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-sm">
                              {reward}
                              <button
                                type="button"
                                onClick={() => removeReward(index)}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                <X size={14} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Social Style */}
                  <div className="space-y-4 pt-4 border-t">
                    <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Social Style</h3>
                    <div className="space-y-2">
                      <Label>Social Style</Label>
                      <p className="text-sm text-gray-600 mb-2">Select the option that best describes the child's general social behavior:</p>
                      <div className="space-y-2">
                        {['Quiet / reflective', 'Outgoing / sociable', 'A mix of both'].map((option) => (
                          <label key={option} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              name="socialStyle"
                              value={option}
                              checked={socialStyle === option}
                              onChange={(e) => setSocialStyle(e.target.value)}
                              className="w-4 h-4 text-action-blue focus:ring-action-blue"
                            />
                            <span className="text-sm text-gray-700">{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Approach to New Things</Label>
                      <p className="text-sm text-gray-600 mb-2">Choose how the child typically responds to new activities or situations:</p>
                      <div className="space-y-2">
                        {['Cautious', 'Adventurous'].map((option) => (
                          <label key={option} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              name="approachToNewThings"
                              value={option}
                              checked={approachToNewThings === option}
                              onChange={(e) => setApproachToNewThings(e.target.value)}
                              className="w-4 h-4 text-action-blue focus:ring-action-blue"
                            />
                            <span className="text-sm text-gray-700">{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Preferred Learning/Work Style</Label>
                      <p className="text-sm text-gray-600 mb-2">Select the child's preferred way of working or learning:</p>
                      <div className="space-y-2">
                        {['Prefers working alone', 'Prefers learning/working together with others'].map((option) => (
                          <label key={option} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              name="preferredLearningWorkStyle"
                              value={option}
                              checked={preferredLearningWorkStyle === option}
                              onChange={(e) => setPreferredLearningWorkStyle(e.target.value)}
                              className="w-4 h-4 text-action-blue focus:ring-action-blue"
                            />
                            <span className="text-sm text-gray-700">{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Frustration Tolerance</Label>
                      <p className="text-sm text-gray-600 mb-2">Choose the level that best represents how the child handles frustration:</p>
                      <div className="space-y-2">
                        {['Low', 'Moderate', 'High'].map((option) => (
                          <label key={option} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              name="frustrationTolerance"
                              value={option}
                              checked={frustrationTolerance === option}
                              onChange={(e) => setFrustrationTolerance(e.target.value)}
                              className="w-4 h-4 text-action-blue focus:ring-action-blue"
                            />
                            <span className="text-sm text-gray-700">{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Motivation Style</Label>
                      <p className="text-sm text-gray-600 mb-2">Select what most often motivates the child:</p>
                      <div className="space-y-2">
                        {['Praise', 'Mastery', 'Curiosity', 'Competition', 'Creative freedom'].map((option) => (
                          <label key={option} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              value={option}
                              checked={motivationStyle.includes(option)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setMotivationStyle([...motivationStyle, option]);
                                } else {
                                  setMotivationStyle(motivationStyle.filter(item => item !== option));
                                }
                              }}
                              className="w-4 h-4 text-action-blue focus:ring-action-blue rounded"
                            />
                            <span className="text-sm text-gray-700">{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 pt-4 border-t">
                    <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Curriculum</h3>
                    <div className="space-y-2 max-w-md">
                      <Label htmlFor="educationSystem">Education system</Label>
                      <p className="text-sm text-gray-600 mb-2">
                        Key stage and year options when adding topics to this child&apos;s plan. Cannot be changed
                        after topics exist in their curriculum.
                      </p>
                      <select
                        id="educationSystem"
                        className={cn(
                          'w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed',
                          getVisibleEditStudentFieldError(
                            'educationSystemKey',
                            showErrorsFor,
                            formErrors.educationSystemKey
                          ) && 'border-destructive-action'
                        )}
                        value={educationSystemKey}
                        disabled={curriculumHasTopics}
                        onChange={(e) => {
                          const k = e.target.value;
                          setEducationSystemKey(k);
                          const opt = educationSystemOptions.find((o) => o.key === k);
                          setEducationSystemDisplayName(opt?.displayName || '');
                        }}
                        onBlur={() => markFieldTouched('educationSystemKey')}
                      >
                        {educationSystemOptions.length === 0 ? (
                          <option value="" disabled>
                            Loading…
                          </option>
                        ) : (
                          educationSystemOptions.map((o) => (
                            <option key={o.key} value={o.key}>
                              {o.displayName}
                            </option>
                          ))
                        )}
                      </select>
                      {getVisibleEditStudentFieldError(
                        'educationSystemKey',
                        showErrorsFor,
                        formErrors.educationSystemKey
                      ) && (
                        <p className="text-sm text-destructive-action">{formErrors.educationSystemKey}</p>
                      )}
                      {curriculumHasTopics && (
                        <p className="text-sm text-amber-800">
                          This student has topics in their curriculum. Remove all topics from their curriculum to change
                          the education system.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Assessment Settings */}
                  <div className="space-y-4 pt-4 border-t">
                    <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Assessment Settings</h3>
                    <div className="space-y-2">
                      <Label htmlFor="passFailThreshold">Pass/Fail Threshold (%)</Label>
                      <p className="text-sm text-gray-600 mb-2">
                        Set the minimum percentage score required to pass assessments. Scores below this threshold will be displayed in orange, scores at or above will be displayed in green.
                      </p>
                      <Input
                        id="passFailThreshold"
                        type="number"
                        min="0"
                        max="100"
                        value={passFailThreshold}
                        onChange={(e) => setPassFailThreshold(e.target.value)}
                        onBlur={() => markFieldTouched('passFailThreshold')}
                        placeholder="e.g., 70"
                        className={cn(
                          'w-full max-w-xs',
                          getVisibleEditStudentFieldError(
                            'passFailThreshold',
                            showErrorsFor,
                            formErrors.passFailThreshold
                          ) && 'border-destructive-action'
                        )}
                      />
                      {getVisibleEditStudentFieldError(
                        'passFailThreshold',
                        showErrorsFor,
                        formErrors.passFailThreshold
                      ) && (
                        <p className="text-sm text-destructive-action">{formErrors.passFailThreshold}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 pt-4 border-t">
                    <Label>Tags</Label>
                    <p className="text-sm text-gray-600 mb-4">
                      Select tags to categorize and organize this student.
                    </p>
                    
                    {/* Selected Tags */}
                    {selectedTags.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Selected Tags:</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedTags.map(tag => (
                            <Badge
                              key={tag.id}
                              variant="secondary"
                              className="inline-flex items-center gap-1 px-3 py-1"
                            >
                              {tag.title}
                              <button
                                type="button"
                                onClick={() => handleTagToggle(tag)}
                                className="hover:bg-gray-300 rounded-full p-0.5"
                              >
                                <X size={14} />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Available Tags */}
                    {availableTags.length > 0 ? (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-gray-700">Available Tags:</p>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setShowCreateTag(true)}
                            className="text-xs"
                          >
                            <Plus size={14} className="mr-1" />
                            Create New Tag
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {availableTags
                            .filter(tag => !selectedTags.some(t => t.id === tag.id))
                            .map(tag => (
                              <button
                                key={tag.id}
                                type="button"
                                onClick={() => handleTagToggle(tag)}
                                className="inline-flex items-center bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-gray-200 transition-colors"
                              >
                                {tag.title}
                              </button>
                            ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-sm text-gray-500 mb-3">No tags available yet.</p>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowCreateTag(true)}
                        >
                          <Plus size={16} className="mr-2" />
                          Create Your First Tag
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => location.href = '#/app/profiles/students'}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={!isFormValid}>Save Changes</Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {showCreateTag && (
              <CreateTagDialog
                onClose={() => setShowCreateTag(false)}
                onCreate={handleCreateTag}
              />
            )}

            {showUnarchiveStudentDialog && unarchiveStudentId && (
              <UnarchiveStudentDialog
                studentId={unarchiveStudentId}
                studentName={unarchiveStudentName}
                onClose={() => {
                  setShowUnarchiveStudentDialog(false);
                  setUnarchiveStudentId(null);
                  setUnarchiveStudentName('');
                }}
              />
            )}
          </div>
      );
  }
}

